import {
  createRepositoryResponseSchema,
  repositoriesResponseSchema,
  type ApiError,
  type CreateRepository,
  type CreateRepositoryResponse,
  type RepositoriesResponse,
  type SessionUser,
} from '@trove/contracts';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = (await response
      .json()
      .catch(() => ({ error: 'request_failed', message: response.statusText }))) as ApiError;
    throw new ApiClientError(response.status, body.error, body.message);
  }
  return response.json() as Promise<T>;
}

export const getSession = () => api<{ user: SessionUser }>('/v1/session');

export const getRepositories = async (): Promise<RepositoriesResponse> =>
  repositoriesResponseSchema.parse(await api<unknown>('/v1/repositories'));

export const createRepository = async (body: CreateRepository): Promise<CreateRepositoryResponse> =>
  createRepositoryResponseSchema.parse(
    await api<unknown>('/v1/repositories', { method: 'POST', body: JSON.stringify(body) }),
  );

export const postJson = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const putJson = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });

export const deleteJson = <T>(path: string) => api<T>(path, { method: 'DELETE' });
